import './index.scss';
import LogoColor from 'src/assets/svg/logo_color.svg?react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Col, Collapse, Form, Input, Row, Spin } from 'antd';
import { CloseCircleOutlined, InfoCircleOutlined, LockOutlined } from '@ant-design/icons';
import { nameof } from 'src/shared/utils/objects';
import { IconChevronLeft } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { Title, Text, Paragraph } from 'src/shared/components/typography';
import { isNullOrWhiteSpaces } from 'src/shared/utils/strings';
import { useAuth } from 'react-oidc-context';

interface ForgotPasswordFormValues {
  password: string;
  confirmPassword: string;
}

const ResetPasswordView: React.FC<any> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const location = useLocation();

  const qs = new URLSearchParams(location?.search ?? '');
  const userName = qs.get('user') as string;
  const code = qs.get('code') as string;

  const isQsOk = !isNullOrWhiteSpaces(userName) && !isNullOrWhiteSpaces(code);

  const [viewState, setViewState] = useState<'WrongData' | 'WaitingInput' | 'Success'>(isQsOk ? 'WaitingInput' : 'WrongData');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>();

  const onFinish = async (values: ForgotPasswordFormValues) => {
    setErrorMsg(undefined);
    setIsLoading(true);

    try {
      const result = await axios.post<any>('/account/resetPassword', {
        userName: userName,
        password: values.password,
        confirmPassword: values.confirmPassword,
        code: code
      });
      setViewState('Success');
      if (result.status !== 200) {
        console.error('ResetPassword', result);
      }
    } catch (e) {
      console.error('ResetPassword', e);
      const axiosError = e as AxiosError<{ message: string }>;
      setViewState('WaitingInput');
      setErrorMsg(axiosError?.response?.data?.message?.toString() ?? (t('Error inesperado') as string));
    } finally {
      setIsLoading(false);
    }
  };

  const [password, setPassword] = useState<string>('');
  const [meter, setMeter] = useState<boolean>((password || '').length > 0);
  const [confirmPassword, setConfirmPassword] = useState('');

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const atLeastOneUppercase = /[A-Z]/g; // capital letters from A to Z
  const atLeastOneLowercase = /[a-z]/g; // small letters from a to z
  const atLeastOneNumeric = /[0-9]/g; // numbers from 0 to 9
  const atLeastOneSpecialChar = /[~@#_^*%/.+:;=,!?<>()[\]{}'"\\\-&|$]/g; // any of the special characters within the square brackets
  const eightCharsOrMore = /.{8,}/g; // eight characters or more

  const passwordTracker = {
    uppercase: password.match(atLeastOneUppercase),
    lowercase: password.match(atLeastOneLowercase),
    number: password.match(atLeastOneNumeric),
    specialChar: password.match(atLeastOneSpecialChar),
    eightCharsOrGreater: password.match(eightCharsOrMore)
  };

  const passwordStrength = Object.values(passwordTracker).filter((value) => value).length;

  const buildMessage = () => {
    if (passwordStrength >= 5) return '';
    const parts: string[] = [];

    if (!passwordTracker.uppercase) parts.push(t('una mayúscula'));
    if (!passwordTracker.lowercase) parts.push(t('una minúscula'));
    if (!passwordTracker.specialChar) parts.push(t('un caracter especial'));
    if (!passwordTracker.number) parts.push(t('un número'));
    if (!passwordTracker.eightCharsOrGreater) parts.push(t('ocho caracteres o más'));

    const last = parts.pop();
    return t('Debe contener al menos ') + parts.join(', ') + (parts.length > 0 ? t(' y ') : '') + last;
  };

  useEffect(() => {
    const validatePassword = (password: string, confirmPassword: string) => {
      return password === confirmPassword && passwordStrength >= 5;
    };
    setIsButtonDisabled(!validatePassword(password, confirmPassword));
  }, [password, confirmPassword, passwordStrength]);

  return (
    <div id="reset-password-container">
      <div id="logo-container">
        <span title="back-cta" role="img" id="back-cta" className={isLoading ? 'anticon disabled' : 'anticon'}>
          <IconChevronLeft
            onClick={isLoading ? undefined : () => navigate('/')}
            size={20}
            strokeWidth={3}
            color={isLoading ? 'lightgrey' : '#22817a'}
          />
        </span>
        <LogoColor />
      </div>
      <div id="form-container">
        <Spin spinning={isLoading} size={'large'}>
          <Card>
            {viewState === 'WrongData' && (
              <>
                <Title style={{ fontSize: 24, marginBottom: 32 }} level={4}>
                  {t('Cambiar contraseña')}
                </Title>
                <Title style={{ fontSize: 18, marginBottom: 8 }} level={4}>
                  <CloseCircleOutlined style={{ color: 'red' }} /> {t('Datos incorrectos')}
                </Title>
                <Paragraph>
                  <Text style={{ fontSize: 16 }}>
                    {t('Ha ocurrido un error. Es posible que hayas usado un link incorrecto. Vuélvelo a intentar o haz una nueva solicitud.')}
                  </Text>
                </Paragraph>

                <Row style={{ paddingTop: 26 }}>
                  <Col span={24}>
                    <Button shape="round" style={{ width: '100%' }} size="large" type="primary" onClick={() => navigate('/forgot-password')}>
                      {t('Solicitar cambiar contraseña')}
                    </Button>
                  </Col>
                </Row>
              </>
            )}
            {viewState === 'Success' && (
              <>
                <Title style={{ fontSize: 24, marginBottom: 16 }} level={4}>
                  {t('Contraseña actualizada')}
                </Title>
                <Paragraph>
                  <Text style={{ fontSize: 16 }}>{t('Ya puedes acceder a tu cuenta con la nueva contraseña que acabas de crear.')}</Text>
                </Paragraph>

                <Row style={{ paddingTop: 22 }}>
                  <Col span={24}>
                    <Button shape="round" style={{ width: '100%' }} size="large" type="primary" onClick={() => auth.signinRedirect()}>
                      {t('Volver al inicio de sesión')}
                    </Button>
                  </Col>
                </Row>
              </>
            )}
            {viewState === 'WaitingInput' && (
              <>
                <Title style={{ fontSize: 24, marginBottom: 29 }} level={4}>
                  {t('Cambia ahora tu contraseña')}
                </Title>
                {(errorMsg || '').trim().length > 0 && (
                  <Row style={{ marginTop: 24 }}>
                    <Col span={24}>
                      <Alert closable onClose={() => setErrorMsg(undefined)} description={errorMsg} type="error" />
                    </Col>
                  </Row>
                )}

                <Form id="resetPassword" name="resetPassword" style={{ maxWidth: '100%', marginTop: 27 }} onFinish={onFinish}>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        style={{ marginBottom: 0 }}
                        extra={
                          meter && (
                            <div>
                              <div className="password-strength-meter"></div>
                              <div>{buildMessage()}</div>
                            </div>
                          )
                        }
                        name={nameof<ForgotPasswordFormValues>('password')}
                        rules={[{ required: true, message: t('Por favor ingrese una contraseña') as string }]}
                      >
                        <Input.Password
                          className="j2l-form-big-input"
                          disabled={isLoading}
                          size="large"
                          placeholder={t('Nueva contraseña') as string}
                          prefix={<LockOutlined />}
                          onFocus={() => setMeter(true)}
                          onChange={(v) => setPassword(v.target.value)}
                          type="password"
                          name="password"
                          id="password"
                          autoComplete="new-password"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        style={{ marginBottom: 28, marginTop: 32 }}
                        name={nameof<ForgotPasswordFormValues>('confirmPassword')}
                        rules={[
                          { required: true, message: t('¡Por favor repite la contraseña!') as string },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error(t('Las contraseñas no coinciden') as string));
                            }
                          })
                        ]}
                      >
                        <Input.Password
                          className="j2l-form-big-input"
                          disabled={isLoading}
                          size="large"
                          placeholder={t('Repite nueva contraseña') as string}
                          prefix={<LockOutlined />}
                          type="password"
                          onChange={(v) => setConfirmPassword(v.target.value)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row style={{ marginBottom: 33 }}>
                    <Col span={24}>
                      <Collapse
                        className="password-suggestions-collapse-2"
                        size="large"
                        ghost
                        expandIcon={() => <InfoCircleOutlined style={{ fontSize: 16 }} />}
                        items={[
                          {
                            key: '1',
                            label: (
                              <Text fontSize={16} underline>
                                {t('Crea una contraseña indescifrable')}
                              </Text>
                            ),
                            children: (
                              <ul>
                                <li>{t('Mejor única, sólo para jobs2live.')}</li>
                                <li>{t('Qué sea larga, cuanto más mejor. Y que combine letras, números y símbolos.')}</li>
                                <li>{t('No uses información personal como fechas o nombres.')}</li>
                                <li>{t('Una buena opción es usar una frase fácil de recordar. Por ejemplo "LaT0rtilla,SiempreConCebolla".')}</li>
                              </ul>
                            )
                          }
                        ]}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col span={24}>
                      <Button
                        disabled={isLoading || isButtonDisabled}
                        shape="round"
                        style={{
                          width: '100%',
                          border: 'none',
                          backgroundColor: isLoading || isButtonDisabled ? '#F1EDE9' : undefined,
                          color: isLoading || isButtonDisabled ? '#CBCED4' : undefined
                        }}
                        size="large"
                        type="primary"
                        htmlType="submit"
                      >
                        {t('Cambia la contraseña')}
                      </Button>
                    </Col>
                  </Row>
                  <style>
                    {`
                  .password-strength-meter::before {
                    background-color: ${['red', 'orange', '#03a2cc', '#03a2cc', '#0ce052'][passwordStrength - 1] || ''};
                    width: ${(passwordStrength / 5) * 100}%;
                  }
                `}
                  </style>
                </Form>
              </>
            )}
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default ResetPasswordView;
